import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, Mail, Calendar, Clock, AlertTriangle, 
  Check, X, User, Pause, Play, MessageSquare, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INTEREST_COLORS = {
  alto: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'Alto - Decidido' },
  medio: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', label: 'Medio - Con objeciones' },
  bajo: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300', label: 'Bajo - Solo preguntó' }
};

const STATUS_CONFIG = {
  activo: { label: 'Activo', color: 'bg-blue-500', textColor: 'text-blue-600' },
  operado: { label: 'Operado', color: 'bg-green-500', textColor: 'text-green-600' },
  desistio: { label: 'Desistió', color: 'bg-red-500', textColor: 'text-red-600' },
  en_pausa: { label: 'En pausa', color: 'bg-slate-400', textColor: 'text-slate-600' }
};

const CONTACT_RESULTS = [
  { value: 'contesto', label: 'Contestó', icon: '✓' },
  { value: 'no_contesto', label: 'No contestó', icon: '✗' },
  { value: 'buzon', label: 'Buzón', icon: '📱' },
  { value: 'agendo_consulta', label: 'Agendó consulta', icon: '📅' },
  { value: 'agendo_cirugia', label: 'Agendó cirugía', icon: '🏥' },
  { value: 'pidio_tiempo', label: 'Pidió tiempo', icon: '⏳' },
  { value: 'desistio', label: 'Desistió', icon: '🚫' }
];

const CONTACT_METHODS = [
  { value: 'llamada', label: 'Llamada', icon: '📞' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { value: 'correo', label: 'Correo', icon: '✉️' },
  { value: 'presencial', label: 'Presencial', icon: '👤' }
];

const SurgeryInterestedDetailPage = () => {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [interested, setInterested] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);

  const [newContact, setNewContact] = useState({
    medio: 'llamada',
    resultado: '',
    notas: '',
    nivel_interes_actualizado: ''
  });

  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchInterested = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surgery-crm/interested/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterested(response.data);
    } catch (error) {
      console.error('Error fetching interested:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surgery-crm/interested/${id}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [id, token]);

  useEffect(() => {
    fetchInterested();
    fetchContacts();
  }, [fetchInterested, fetchContacts]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      await axios.put(
        `${API}/surgery-crm/interested/${id}`,
        { estado: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Estado actualizado');
      fetchInterested();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleUpdateInterest = async (newLevel) => {
    try {
      await axios.put(
        `${API}/surgery-crm/interested/${id}`,
        { nivel_interes: newLevel },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Nivel de interés actualizado');
      fetchInterested();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.resultado || !newContact.notas || newContact.notas.length < 5) {
      toast.error('Selecciona un resultado y escribe notas (mínimo 5 caracteres)');
      return;
    }
    try {
      await axios.post(
        `${API}/surgery-crm/interested/${id}/contacts`,
        newContact,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Contacto registrado');
      setShowContactModal(false);
      setNewContact({ medio: 'llamada', resultado: '', notas: '', nivel_interes_actualizado: '' });
      fetchInterested();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar contacto');
    }
  };

  const handleSaveObservaciones = async (observaciones) => {
    try {
      await axios.put(
        `${API}/surgery-crm/interested/${id}`,
        { observaciones_generales: observaciones },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Observaciones guardadas');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleSendEmailReminder = async () => {
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/email/send-surgery-reminder/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Recordatorio enviado por email');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar email';
      if (errorMsg.includes('no configurado') || errorMsg.includes('API Key')) {
        toast.error('Configura primero el email en Configuración → Notificaciones Email');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <p className="text-slate-500">Cargando...</p>
      </div>
    );
  }

  if (!interested) {
    return (
      <div className="p-6 flex items-center justify-center">
        <p className="text-slate-500">Paciente no encontrado</p>
      </div>
    );
  }

  const interestStyle = INTEREST_COLORS[interested.nivel_interes] || INTEREST_COLORS.medio;
  const statusConfig = STATUS_CONFIG[interested.estado] || STATUS_CONFIG.activo;
  const isOverdue = interested.esta_vencido;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/admin/crm-cirugias')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver al listado
      </Button>

      {/* Overdue Banner */}
      {isOverdue && interested.estado === 'activo' && (
        <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">
            Este paciente debió contactarse hace {interested.dias_desde_ultimo_contacto - 15} días
          </span>
        </div>
      )}

      {/* Multiple attempts warning */}
      {interested.contador_contactos_sin_respuesta >= 3 && (
        <div className="bg-amber-100 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>
            <strong>⚠ {interested.contador_contactos_sin_respuesta}+ intentos sin respuesta.</strong>{' '}
            Considera cambiar el medio de contacto o revisar los datos.
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="col-span-2 space-y-6">
          {/* Header Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="font-display text-2xl text-slate-900">{interested.nombre_completo}</h1>
                  <p className="text-lg text-slate-600 mt-1">{interested.tipo_cirugia_nombre}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {interested.telefono}
                    </span>
                    {interested.correo && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {interested.correo}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={`${statusConfig.color} text-white`}>
                    {statusConfig.label}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Button */}
          <Button 
            onClick={() => setShowContactModal(true)} 
            className="w-full bg-[#c2410c] hover:bg-[#a13308] text-white py-6 text-lg"
          >
            <Phone className="w-5 h-5 mr-2" />
            Registrar contacto
          </Button>

          {/* Contact Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">Historial de contactos</CardTitle>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Sin contactos registrados</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact, idx) => {
                    const method = CONTACT_METHODS.find(m => m.value === contact.medio);
                    const result = CONTACT_RESULTS.find(r => r.value === contact.resultado);
                    return (
                      <div key={contact.id} className="relative pl-6 pb-4 border-l-2 border-slate-200 last:border-0">
                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-slate-300" />
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-slate-900">
                                {method?.icon} {method?.label}
                              </span>
                              <span className="text-slate-400">•</span>
                              <span className={result?.value === 'contesto' ? 'text-green-600' : 'text-slate-600'}>
                                {result?.icon} {result?.label}
                              </span>
                            </div>
                            <p className="text-slate-700 mt-1">{contact.notas}</p>
                            {contact.nivel_interes_actualizado && (
                              <Badge className="mt-2 bg-blue-100 text-blue-700">
                                Nivel cambiado a: {contact.nivel_interes_actualizado}
                              </Badge>
                            )}
                          </div>
                          <div className="text-right text-xs text-slate-400">
                            <div>{format(parseISO(contact.fecha_contacto), "dd/MM/yyyy")}</div>
                            <div>{contact.registrado_por_nombre}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Observaciones */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg">Observaciones generales</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                defaultValue={interested.observaciones_generales || ''}
                onBlur={(e) => {
                  if (e.target.value !== interested.observaciones_generales) {
                    handleSaveObservaciones(e.target.value);
                  }
                }}
                placeholder="Notas adicionales sobre este paciente..."
                rows={4}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Interest Level */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Nivel de interés</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={interested.nivel_interes} onValueChange={handleUpdateInterest}>
                <SelectTrigger className={`${interestStyle.bg} ${interestStyle.text} ${interestStyle.border}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alto">Alto - Decidido</SelectItem>
                  <SelectItem value="medio">Medio - Con objeciones</SelectItem>
                  <SelectItem value="bajo">Bajo - Solo preguntó</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Registro</span>
                <span className="font-medium">{format(parseISO(interested.fecha_registro), "dd/MM/yyyy")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Último contacto</span>
                <span className={`font-medium ${interested.dias_desde_ultimo_contacto > 15 ? 'text-red-600' : ''}`}>
                  {interested.fecha_ultimo_contacto 
                    ? `${format(parseISO(interested.fecha_ultimo_contacto), "dd/MM/yyyy")} (hace ${interested.dias_desde_ultimo_contacto}d)`
                    : 'Sin contacto'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Próximo contacto</span>
                <span className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                  {interested.fecha_proximo_contacto 
                    ? format(parseISO(interested.fecha_proximo_contacto), "dd/MM/yyyy")
                    : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Intentos sin respuesta</span>
                <span className={`font-medium ${interested.contador_contactos_sin_respuesta >= 3 ? 'text-red-600' : ''}`}>
                  {interested.contador_contactos_sin_respuesta}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {interested.estado === 'activo' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Acciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  onClick={handleSendEmailReminder}
                  disabled={sendingEmail}
                  variant="outline"
                  className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingEmail ? 'Enviando...' : 'Enviar recordatorio email'}
                </Button>
                <Button 
                  onClick={() => handleUpdateStatus('operado')} 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Marcar como operado
                </Button>
                <Button 
                  onClick={() => handleUpdateStatus('desistio')} 
                  variant="outline"
                  className="w-full text-red-600 border-red-300 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Marcar como desistió
                </Button>
                <Button 
                  onClick={() => handleUpdateStatus('en_pausa')} 
                  variant="outline"
                  className="w-full"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pausar seguimiento
                </Button>
              </CardContent>
            </Card>
          )}

          {interested.estado === 'en_pausa' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleUpdateStatus('activo')} 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Reactivar seguimiento
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Assigned */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm">
                <span className="text-slate-500">Asignado a:</span>
                <span className="ml-2 font-medium">{interested.asignado_a_nombre || 'Sin asignar'}</span>
              </div>
              <div className="text-sm mt-2">
                <span className="text-slate-500">Creado por:</span>
                <span className="ml-2 font-medium">{interested.creado_por_nombre}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Registrar contacto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Medio de contacto</Label>
                <Select value={newContact.medio} onValueChange={(v) => setNewContact(prev => ({ ...prev, medio: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.icon} {m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resultado</Label>
                <Select value={newContact.resultado} onValueChange={(v) => setNewContact(prev => ({ ...prev, resultado: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_RESULTS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.icon} {r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas del contacto (obligatorio)</Label>
              <Textarea
                value={newContact.notas}
                onChange={(e) => setNewContact(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="¿Qué dijo el paciente? ¿Objeciones? ¿Próximos pasos?"
                rows={4}
              />
            </div>
            <div>
              <Label>¿Actualizar nivel de interés?</Label>
              <Select 
                value={newContact.nivel_interes_actualizado} 
                onValueChange={(v) => setNewContact(prev => ({ ...prev, nivel_interes_actualizado: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mantener actual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Mantener actual</SelectItem>
                  <SelectItem value="alto">Subir a Alto</SelectItem>
                  <SelectItem value="medio">Cambiar a Medio</SelectItem>
                  <SelectItem value="bajo">Bajar a Bajo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateContact} className="bg-[#c2410c] hover:bg-[#a13308] text-white">
              Guardar contacto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurgeryInterestedDetailPage;
